import { useDebounce } from "@visiblethread/react-components";
import clsx from "clsx";
import { get, isFunction } from "lodash";
import { func, number } from "prop-types";
import { useCallback, useContext, useEffect, useState } from "react";

import CellErrorIcon from "./CellErrorIcon";
import CellInput from "./CellInput";
import ChevronButton from "./ChevronButton";
import { TanStackRowProps } from "./constants";
import DraggableButton from "./DraggableButton";
import classes from "./ExcelTableCell.module.scss";
import ExcelTableContext from "./ExcelTableContext";

const ON_VALUE_CHANGE_DEBOUNCE_MS = 150;

const ExcelTableCell = ({
  colIndex,
  getValue,
  isCellDisabled,
  row,
  ...restOfRowData
}) => {
  const {
    cellValidationErrors,
    commitEditOnChange,
    currentCellId,
    doubleClickToEdit,
    draggableColumnIndex,
    expandableColumnIndex,
    getCellDepth,
    indentAtColumnIndex,
    isEditing: isEditingTable,
    isDraggable,
    onSubmitCellEdit,
    rowIdKey,
    sanitizeUserInput,
    setCurrentCellId,
    validateEditableCellClick,
  } = useContext(ExcelTableContext);
  const [isEditing, setIsEditing] = useState(false);

  const { column } = restOfRowData;
  const { editKey, meta, originalCell } = column?.columnDef ?? {};
  const isDraggableCell = draggableColumnIndex === colIndex;
  const isExpandableCell = expandableColumnIndex === colIndex;
  const isExpanded = row.getIsExpanded();
  const isDisabled = isCellDisabled?.(row.original, row.index, row);

  const cellId = restOfRowData?.cell?.id;
  const depth = getCellDepth(row);
  const hasChildren = row.getCanExpand();
  const onClickToggleRow = row.getToggleExpandedHandler();
  const indentDepth =
    indentAtColumnIndex === colIndex
      ? `calc(var(--spacing-800) * ${depth})`
      : "0px";

  const finalEditKey =
    typeof editKey === "function" ? editKey({ row }) : editKey;
  const valueToEdit = finalEditKey ? row.original[finalEditKey] || "" : "";
  const { isNew } = row.original;
  const isEditableCell = isEditingTable && Boolean(finalEditKey);

  const rowId = get(row.original, rowIdKey);
  const cellError = get(cellValidationErrors, [rowId, finalEditKey], null);

  useEffect(() => {
    if (!isEditingTable) {
      setIsEditing(false);
    }
  }, [isEditingTable]);

  useEffect(() => {
    const isCurrentCell = cellId === currentCellId;
    // This should trigger an autofocus effect, given we only
    // even setCurrentCellId via the Parent implementing ExcelTable
    if (isEditableCell && isCurrentCell && isNew) {
      setIsEditing(true);
      setCurrentCellId(null);
    }
  }, [cellId, currentCellId, isEditableCell, isNew, valueToEdit]);

  const getPlaceholder = useCallback(() => {
    if (!meta?.placeholder) {
      return "";
    }

    return isFunction(meta.placeholder)
      ? meta.placeholder(row)
      : meta.placeholder;
  }, [meta, row]);

  const onBlur = (value) => {
    setIsEditing(false);
    onSubmitCellEdit({ valueKey: finalEditKey, row, value });
  };

  const onClickExpand = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClickToggleRow(row);
  };

  const onDoubleClick = () => {
    if (!isDisabled && isEditableCell && validateEditableCellClick(row)) {
      setIsEditing(true);
    }
  };

  const onFocusDummy = useCallback(() => {
    if (!isDisabled) {
      setIsEditing(true);
    }
  }, [isDisabled]);

  const onSubmitCellChange = (value) => {
    setIsEditing(false);
    onSubmitCellEdit({ valueKey: finalEditKey, row, value });
  };

  const onValueChange = useDebounce(
    (value) => {
      onSubmitCellEdit({ valueKey: finalEditKey, row, value });
    },
    ON_VALUE_CHANGE_DEBOUNCE_MS,
    [finalEditKey, row, onSubmitCellEdit]
  );

  const renderContent = () => {
    if (isEditing) {
      return (
        <CellInput
          cellId={cellId}
          onBlur={onBlur}
          onSubmit={onSubmitCellChange}
          onValueChange={commitEditOnChange ? onValueChange : undefined}
          placeholder={getPlaceholder()}
          sanitizeUserInput={sanitizeUserInput}
          value={valueToEdit}
        />
      );
    }

    return (
      <div
        className={clsx(classes.cellValue, {
          [classes["cellValue--disabled"]]: isDisabled,
          [classes["cellValue--editable"]]: isEditableCell,
        })}
      >
        {originalCell
          ? originalCell({ getValue, row, ...restOfRowData })
          : getValue()}
      </div>
    );
  };

  const onEditHandler = doubleClickToEdit ? "onDoubleClick" : "onClick";
  const editHandlerProps = {
    [onEditHandler]: onDoubleClick,
  };

  return (
    <div
      className={clsx(classes["excel_table_cell"], {
        [classes["excel_table_cell--editable"]]: isEditableCell,
        [classes["excel_table_cell--editing"]]: isEditing,
        [classes["excel_table_cell--error"]]: Boolean(cellError),
      })}
      data-excel-cell-depth={depth}
      data-excel-cell-editkey={finalEditKey}
      style={{
        "--cell-indent-depth": indentDepth,
      }}
    >
      {isEditingTable && isDraggable && isDraggableCell && (
        <DraggableButton isEditableCell={isEditableCell} row={row} />
      )}

      <div
        className={classes["excel_table_cell__content"]}
        data-testid={cellId}
        data-cell-id={cellId}
        {...editHandlerProps}
      >
        {isExpandableCell && (
          <ChevronButton
            isEnabled={hasChildren}
            isExpanded={isExpanded}
            onClick={onClickExpand}
          />
        )}

        {renderContent()}

        {Boolean(cellError) && <CellErrorIcon message={cellError} />}
      </div>

      {isEditableCell && !isEditing && (
        <input
          className={classes["excel_table_cell__dummy"]}
          disabled={isDisabled}
          id={`dummy-input-${cellId}`}
          type="text"
          onFocus={onFocusDummy}
          data-clarity-mask="True"
        />
      )}
    </div>
  );
};

ExcelTableCell.propTypes = {
  colIndex: number,
  getValue: func,
  isCellDisabled: func,
  row: TanStackRowProps,
};

export default ExcelTableCell;
